import type { useStytch } from "../utils/useStytch";

type StytchClient = ReturnType<typeof useStytch>;

const PATIENT_CLAIM = "patient_id_in_dashboard_api";
const CLAIM_MAX_RETRIES = 15;
const CLAIM_RETRY_DELAY_MS = 500;

/**
 * Wait for the backend to provision the patient claim on the Stytch user,
 * refreshing the cached user between attempts. A brand-new account has no
 * claim for a moment after sign-up, and nothing else refreshes the user
 * after an OAuth callback. Resolves true once the claim is present, false
 * when the retry window elapses or `isCancelled` reports true.
 */
export async function waitForPatientClaim(
  stytch: StytchClient,
  isCancelled: () => boolean = () => false,
): Promise<boolean> {
  const hasClaim = () =>
    Boolean((stytch.user.getSync()?.trusted_metadata ?? {})[PATIENT_CLAIM]);

  for (let attempt = 0; attempt < CLAIM_MAX_RETRIES; attempt++) {
    if (hasClaim()) return true;
    try {
      await stytch.user.get();
    } catch {
      // Keep retrying; a transient refresh error is not a missing claim.
    }
    if (attempt < CLAIM_MAX_RETRIES - 1) {
      await new Promise((resolve) => setTimeout(resolve, CLAIM_RETRY_DELAY_MS));
    }
    if (isCancelled()) return false;
  }
  return hasClaim();
}
